/**
 * Pure catalog helpers: search, venue filter, sort, price formatting. No React, no I/O.
 * Human titles come from describe/ (#690); search matches the title as well as the raw name and description.
 */
import { RULES, cardLabel, matches, type Rules } from '../classify';
import { describeOutcome, type Category, type OutcomeDescription } from '../describe';
import type { OutcomeSpec } from '../hl/types';
import { cents } from '../i18n/format';
import type { DayVolume } from '../volume/volume';

/** `vol` = 24 h traded volume in USD, highest first (#874). */
export type SortKey = 'id' | 'mid' | 'name' | 'vol';
export const SORT_KEYS: readonly SortKey[] = ['id', 'mid', 'name', 'vol'];
export function isSortKey(v: unknown): v is SortKey {
  return v === 'id' || v === 'mid' || v === 'name' || v === 'vol';
}

/** Venue filter value for outcomes that carry no `venue` field. */
export const NO_VENUE = '(none)';

export interface CatalogItem {
  outcome: number;
  name: string;
  /** Raw description, '' when absent. */
  description: string;
  venue: string | undefined;
  quoteToken: string;
  sides: [string, string];
  /** Side-0 (Yes / A) mid in (0, 1), undefined when there is no price. */
  mid: number | undefined;
  /** Human title / subtitle / category / resolution time (describe/). */
  title: string;
  subtitle?: string;
  category: Category;
  resolvesAt?: Date;
  resolvesLabel?: string;
  /** Price touch market (#786, priceTouch* template): the tile shows a "Touch" badge. */
  touch: boolean;
  /** Question (categorical market) this outcome belongs to, if any. */
  question?: number;
  /** 24 h traded volume (#874, volume/volume.ts dayVolumes); undefined until loaded or when not reported. */
  volume?: DayVolume;
  /** Market tags (#875, classify/), tree order; [] when not classified. */
  tags: readonly string[];
  /** Tag shown as the card label (classify cardLabel), if any. */
  tagLabel?: string;
}

/** `desc` defaults to describeOutcome(o) without question context (pass the describer's result in the app). */
export function toItem(
  o: OutcomeSpec,
  mid: number | undefined,
  desc: OutcomeDescription = describeOutcome(o),
  volume?: DayVolume,
  tags: readonly string[] = [],
  rules: Rules = RULES,
): CatalogItem {
  const description = typeof o.description === 'string' ? o.description : '';
  return {
    outcome: o.outcome,
    name: typeof o.name === 'string' ? o.name : '',
    description,
    venue: o.venue || undefined,
    quoteToken: o.quoteToken ?? '',
    sides: desc.sideLabels,
    mid,
    title: desc.title,
    subtitle: desc.subtitle,
    category: desc.category,
    resolvesAt: desc.resolvesAt,
    resolvesLabel: desc.resolvesLabel,
    touch: desc.touch !== undefined,
    question: desc.question,
    ...(volume ? { volume } : {}),
    tags,
    tagLabel: cardLabel(rules, tags) ?? undefined,
  };
}

export function venueKey(item: Pick<CatalogItem, 'venue'>): string {
  return item.venue ?? NO_VENUE;
}

/** Venue -> count, sorted by count desc then name; NO_VENUE last. */
export function venueCounts(items: readonly CatalogItem[]): [string, number][] {
  const m = new Map<string, number>();
  for (const it of items) m.set(venueKey(it), (m.get(venueKey(it)) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => {
    if (a[0] === NO_VENUE) return 1;
    if (b[0] === NO_VENUE) return -1;
    return b[1] - a[1] || a[0].localeCompare(b[0]);
  });
}

/**
 * Lower-case for search, language-independent: market data is English, so a Turkish keyboard's "İ" (lower-cased to
 * "i" + a combining dot) and "ı" both match a plain "i" (#900).
 */
export function foldSearch(s: string): string {
  return s.toLowerCase().replace(/̇/g, '').replace(/ı/g, 'i');
}

/** Case-insensitive substring match on id, human title/subtitle, name and raw description. "#123" also matches id 123. */
export function matchesQuery(item: CatalogItem, query: string): boolean {
  const q = foldSearch(query.trim());
  if (!q) return true;
  const id = String(item.outcome);
  const qId = q.startsWith('#') ? q.slice(1) : q;
  if (qId && id.includes(qId)) return true;
  return (
    foldSearch(item.title).includes(q) ||
    (item.subtitle !== undefined && foldSearch(item.subtitle).includes(q)) ||
    foldSearch(item.name).includes(q) ||
    foldSearch(item.description).includes(q)
  );
}

export interface CatalogFilter {
  query: string;
  /** Empty = all venues. */
  venues: ReadonlySet<string>;
  sort: SortKey;
  /** Selected market-tag chips (#875); empty / absent = every market. Combined per rules.ui.combine. */
  tags?: readonly string[];
  /** Rules in use (bundled or downloaded); default the bundled copy. */
  rules?: Rules;
}

export function applyFilter(items: readonly CatalogItem[], f: CatalogFilter): CatalogItem[] {
  const sel = f.tags ?? [];
  const rules = f.rules ?? RULES;
  const out = items.filter(
    (it) => (f.venues.size === 0 || f.venues.has(venueKey(it))) && matches(rules, it.tags, sel) && matchesQuery(it, f.query),
  );
  const byIdDesc = (a: CatalogItem, b: CatalogItem) => b.outcome - a.outcome;
  switch (f.sort) {
    case 'mid':
      // Highest Yes probability first; no-price outcomes last.
      return out.sort((a, b) => {
        if (a.mid === undefined && b.mid === undefined) return byIdDesc(a, b);
        if (a.mid === undefined) return 1;
        if (b.mid === undefined) return -1;
        return b.mid - a.mid || byIdDesc(a, b);
      });
    case 'name':
      return out.sort((a, b) => a.title.localeCompare(b.title) || byIdDesc(a, b));
    case 'vol':
      // Most traded (24 h USD) first; outcomes without volume data last.
      return out.sort((a, b) => {
        const av = a.volume?.usd;
        const bv = b.volume?.usd;
        if (av === undefined && bv === undefined) return byIdDesc(a, b);
        if (av === undefined) return 1;
        if (bv === undefined) return -1;
        return bv - av || byIdDesc(a, b);
      });
    default:
      return out.sort(byIdDesc);
  }
}

/** 0.63 -> "63¢"; sub-1¢ and above-99¢ prices keep one decimal (0.004 -> "0.4¢"). */
export function formatCents(mid: number | undefined): string {
  if (mid === undefined || !Number.isFinite(mid)) return '—';
  const c = mid * 100;
  if (c > 0 && c < 1) return cents(c.toFixed(1));
  if (c > 99 && c < 100) return cents(c.toFixed(1));
  return cents(String(Math.round(c)));
}
